import type { Dict, Lang } from "../lib/i18n";
import common from "./common";
import shopping from "./shopping";
import wallet from "./wallet";
import ledger from "./ledger";
import nutrition from "./nutrition";
import community from "./community";
import places from "./places";
import landing from "./landing";
import plans from "./plans";
import fridge from "./fridge";
import goods from "./goods";
import report from "./report";

const all: Dict[] = [common, shopping, wallet, ledger, nutrition, community, places, landing, plans, fridge, goods, report];
const base = (l: "zh" | "en" | "nl") => Object.assign({}, ...all.map((d) => d[l])) as Record<string, string>;

/**
 * Every language's texts. Chinese, English and Dutch are written in the namespace files and always here; the
 * other languages are one folder each under locales/ (translated from the English) and are fetched only when
 * someone uses that language — see loadLang. Until then, and for any text not translated yet, English shows.
 */
export const DICTIONARIES: Record<Lang, Record<string, string>> = {
  zh: base("zh"), en: base("en"), nl: base("nl"),
  fr: {}, de: {}, it: {}, es: {}, pt: {}, cs: {}, pl: {}, hu: {},
};

const files = import.meta.glob<Record<string, string>>("./locales/*/*.json", { import: "default" });
const recipeFiles = import.meta.glob<Record<string, string>>("../pages/fridge/recipes-i18n/*.json", { import: "default" });

/**
 * Recipe names, ingredients ("rx.<English>") and steps ("rxs.<recipe id>", one step per line) in the languages
 * the recipes aren't written in; fetched with the language. See pages/fridge/recipes.ts.
 */
export const RECIPE_TEXT: Partial<Record<Lang, Record<string, string>>> = {};
const loading = new Map<Lang, Promise<void>>();

/** Fetch a language's texts once (a no-op for the built-in languages). */
export function loadLang(lang: Lang): Promise<void> {
  if (lang === "zh" || lang === "en" || lang === "nl") return Promise.resolve();
  let p = loading.get(lang);
  if (!p) {
    const parts = Object.entries(files).filter(([path]) => path.startsWith(`./locales/${lang}/`));
    const recipes = Object.entries(recipeFiles).find(([path]) => path.endsWith(`/${lang}.json`))?.[1];
    p = Promise.all([Promise.all(parts.map(([, load]) => load())), recipes ? recipes().catch(() => ({})) : Promise.resolve({})])
      .then(([dicts, rx]) => {
        DICTIONARIES[lang] = Object.assign({}, ...dicts);
        RECIPE_TEXT[lang] = rx;
      })
      .catch(() => {
        loading.delete(lang); // offline: try again next time; English meanwhile
      });
    loading.set(lang, p);
  }
  return p;
}

export const isLoaded = (lang: Lang) => lang === "zh" || lang === "en" || lang === "nl" || Object.keys(DICTIONARIES[lang]).length > 0;
